import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import BookTaxi from './BookTaxi';

const MODES = {
  instant: {
    id: 'instant',
    hash: '#instant-ride',
    title: 'Instant ride',
    tagline: 'City trips, right now',
    blurb: 'Same-day pickups in town — book in the Saarthi rider app and get matched quickly.',
  },
  hire: {
    id: 'hire',
    hash: '#hire-a-car',
    title: 'Hire a car',
    tagline: 'Outstation & planned trips',
    blurb: 'Long-distance travel, airport runs, and scheduled rides — request here and we assign a driver.',
  },
};

function resolveModeFromHash() {
  const hash = window.location.hash.toLowerCase();
  if (hash === '#instant-ride' || hash === '#instant') return 'instant';
  if (hash === '#hire-a-car' || hash === '#book' || hash === '') return 'hire';
  return 'hire';
}

function RideServices() {
  const [mode, setMode] = useState(resolveModeFromHash);

  const selectMode = useCallback((next) => {
    setMode(next);
    const item = MODES[next];
    if (item) {
      window.history.replaceState(null, '', item.hash);
    }
  }, []);

  useEffect(() => {
    const onHash = () => setMode(resolveModeFromHash());
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  return (
    <section className="ride-services section" id="book" aria-labelledby="ride-services-heading">
      <div className="section__inner ride-services__inner">
        <header className="ride-services__intro">
          <p className="eyebrow">Choose your ride</p>
          <h2 id="ride-services-heading">Instant ride or hire a car — pick what fits your trip.</h2>
          <p className="section__lead">
            City trips start in the app. For outstation and website bookings, fill in the form — our
            team confirms by phone.
          </p>
        </header>

        <div className="ride-services__picker" role="tablist" aria-label="Ride type">
          {Object.values(MODES).map((item) => {
            const active = mode === item.id;
            return (
              <button
                key={item.id}
                type="button"
                role="tab"
                aria-selected={active}
                className={`ride-service-card ${active ? 'ride-service-card--active' : ''} ride-service-card--${item.id}`}
                onClick={() => selectMode(item.id)}
              >
                <span className="ride-service-card__icon" aria-hidden="true">
                  {item.id === 'instant' ? '⚡' : '🚗'}
                </span>
                <span className="ride-service-card__title">{item.title}</span>
                <span className="ride-service-card__tagline">{item.tagline}</span>
                <span className="ride-service-card__blurb">{item.blurb}</span>
              </button>
            );
          })}
        </div>

        <div
          className="ride-services__panel"
          role="tabpanel"
          id={mode === 'instant' ? 'instant-ride-panel' : 'hire-a-car-panel'}
          aria-labelledby={mode === 'instant' ? 'tab-instant' : 'tab-hire'}
        >
          {mode === 'instant' ? (
            <div className="instant-ride-panel" id="instant-ride">
              <div className="instant-ride-panel__grid">
                <article className="instant-ride-panel__steps">
                  <h3>Book in 3 easy steps</h3>
                  <ol>
                    <li>
                      <strong>Open the Saarthi rider app</strong>
                      <span>Sign in with your mobile number.</span>
                    </li>
                    <li>
                      <strong>Choose instant / daily ride</strong>
                      <span>Set pickup and drop in your city.</span>
                    </li>
                    <li>
                      <strong>Confirm and track your driver</strong>
                      <span>We assign an approved driver from our fleet.</span>
                    </li>
                  </ol>
                </article>
                <aside className="instant-ride-panel__aside">
                  <p className="instant-ride-panel__note">
                    Instant rides are handled in the app for live matching and updates. Need help?
                  </p>
                  <Link to="/support" className="btn btn--primary">
                    Contact support
                  </Link>
                  <button
                    type="button"
                    className="btn btn--ghost ride-services__switch"
                    onClick={() => selectMode('hire')}
                  >
                    Need outstation instead? Hire a car →
                  </button>
                </aside>
              </div>
            </div>
          ) : (
            <div className="hire-car-panel" id="hire-a-car">
              <BookTaxi embedded />
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

export default RideServices;
